import { supabase } from "@/integrations/supabase/client";
import type { Permission } from "@/constants/permissions";
import {
  isConfigurablePermission,
  type ConfigurablePermission,
} from "@/lib/memberProjectPermissions";

export async function fetchUserPermissions(
  profileId: number,
  orgId: string
): Promise<Set<Permission>> {
  const { data, error } = await supabase.rpc("get_user_permissions", {
    p_user_id: profileId,
    p_org_id: orgId,
  });

  if (error) throw new Error(error.message);

  const rows = (data ?? []) as Array<{ permission_code: string }>;
  return new Set(rows.map((p) => p.permission_code as Permission));
}

export async function fetchBuildingAssignments(
  profileId: number,
  orgId: string
): Promise<Set<number>> {
  const { data, error } = await supabase
    .from("user_building_assignments")
    .select("building_id")
    .eq("user_id", profileId)
    .eq("org_id", orgId);

  if (error) throw new Error(error.message);

  return new Set((data ?? []).map((a) => a.building_id as number));
}

export type ProjectAssignment = {
  projectId: number;
  orgId: string;
  buildingId: number;
  assignedBy: number;
};

/**
 * The FK is NAMED, not left to PostgREST to infer.
 *
 * `user_project_assignments` has TWO foreign keys to `building_projects` since
 * the folder-level-permissions work added the org tie
 * `(project_id, org_id) → building_projects(id, org_id)` alongside the original
 * `project_id → building_projects(id)`. With two candidates PostgREST refuses
 * the embed outright — `HTTP 300`, `PGRST201 Could not embed because more than
 * one relationship was found` — rather than picking one.
 *
 * That turned this read into a throw, and the damage was silent and wide:
 * `loadAssignments` catches, so `assignedProjects` simply stayed EMPTY, and
 * `usePermissions().hasPermission(p, {projectId})` answers every PROJECT_SCOPED
 * permission out of that map. So a subcontractor lost every project-scoped
 * affordance at once — Rename was just the one that got noticed — while owners
 * and admins saw nothing wrong, because `hasPermission` short-circuits on
 * `isOrgOwner || isOrgAdmin` and their MODIFY_DATA is org-wide.
 *
 * Either FK resolves the join identically; the plain project one is named
 * because that is the relationship this query is actually about.
 */
export async function fetchProjectAssignments(
  profileId: number,
  orgId: string
): Promise<ProjectAssignment[]> {
  const { data, error } = await supabase
    .from("user_project_assignments")
    .select(
      "project_id, org_id, assigned_by, " +
        "building_projects!user_project_assignments_project_id_fkey!inner(building_id)"
    )
    .eq("user_id", profileId)
    .eq("org_id", orgId);

  if (error) throw new Error(error.message);

  const rows = (data ?? []) as Array<{
    project_id: number;
    org_id: string;
    assigned_by: number;
    // `!inner`, so the embed is always present.
    building_projects: { building_id: number };
  }>;
  return rows.map((r) => ({
    projectId: r.project_id,
    orgId: r.org_id,
    buildingId: r.building_projects.building_id,
    assignedBy: r.assigned_by,
  }));
}

/**
 * PostgREST / Postgres codes for "this function does not exist here yet": the
 * app can ship ahead of the franklin-data-pipeline migration that adds it.
 */
const MISSING_FUNCTION_CODES: ReadonlySet<string> = new Set(["PGRST202", "42883"]);

export function isMissingFunctionError(error: { code?: string } | null | undefined): boolean {
  return !!error?.code && MISSING_FUNCTION_CODES.has(error.code);
}

/**
 * What the caller may do in each assigned project, per the configurable codes
 * (`get_my_project_permissions`). It is `has_permission()` itself, so it can
 * never disagree with RLS — including per-member overrides that grant beyond,
 * or take away from, the role's default.
 *
 * `null` means the database predates per-project permissions: the caller then
 * answers project-scoped checks from the role grant and the assignment, as it
 * always did. Any other failure throws, so the assignment load it rides with
 * fails closed and retries.
 */
export async function fetchMyProjectPermissions(
  orgId: string
): Promise<Map<number, Set<ConfigurablePermission>> | null> {
  const { data, error } = await supabase.rpc("get_my_project_permissions", { p_org_id: orgId });

  if (error) {
    if (isMissingFunctionError(error)) return null;
    throw new Error(error.message);
  }

  const byProject = new Map<number, Set<ConfigurablePermission>>();
  const rows = (data ?? []) as Array<{ project_id: number; permission_code: string }>;
  for (const row of rows) {
    if (!isConfigurablePermission(row.permission_code)) continue;
    const set = byProject.get(row.project_id) ?? new Set<ConfigurablePermission>();
    set.add(row.permission_code);
    byProject.set(row.project_id, set);
  }
  return byProject;
}

/**
 * How many folders of `projectId` are hidden from the caller
 * (`user_project_folder_exclusions`). A member may read their own rows (RLS
 * `upfe_select`, the `user_id = me` branch); 0 means they see the whole
 * project's documentation. A head-only count: no ids leave the database.
 */
export async function countMyFolderExclusions(
  userProfileId: number,
  projectId: number
): Promise<number> {
  const { count, error } = await supabase
    .from("user_project_folder_exclusions")
    .select("folder_id", { count: "exact", head: true })
    .eq("user_id", userProfileId)
    .eq("project_id", projectId);
  if (error) throw new Error(error.message);
  return count ?? 0;
}
